import React from "react";
import PropTypes from "prop-types";
import RetryReleaseButton from "./RetryReleaseButton";
import CheckHsbcStatusButton from "./CheckHsbcStatusButton";
import ConfirmNotReceivedButton from "./ConfirmNotReceivedButton";

const when = (iso) => (iso ? String(iso).replace("T", " ").slice(0, 16) : "");

const VIEW = {
  ready: {
    tone: "tw-border-[#abefc6] tw-bg-[#ecfdf3] tw-text-[#05603a]", icon: "bi-shield-check",
    title: "HSBC online payment · file reviewed by Accounting",
    body: (p) => `XML v3 + GPG prepared ${when(p.built_at)} by ${p.built_by} for journal ${p.journal_number}. ` +
      "It matches what you sign. The payment is sent to HSBC and paid immediately after the BOD approves.",
  },
  stale: {
    tone: "tw-border-[#fecdca] tw-bg-[#fef3f2] tw-text-[#912018]", icon: "bi-exclamation-octagon",
    title: "HSBC payment file is out of date",
    body: () => "The payment changed after Accounting prepared the file. It cannot be approved or paid until " +
      "Accounting rebuilds and reviews it.",
  },
  failed: {
    tone: "tw-border-[#fecdca] tw-bg-[#fef3f2] tw-text-[#912018]", icon: "bi-x-octagon",
    title: "HSBC payment file could not be prepared",
    body: (p) => p.error || "Accounting must prepare it before this request moves on.",
  },
  missing: {
    tone: "tw-border-[#fedf89] tw-bg-[#fffaeb] tw-text-[#93370d]", icon: "bi-hourglass-split",
    title: "HSBC online payment · file not prepared yet",
    body: () => "Accounting prepares and reviews the XML v3 + GPG file before Head of Finance and BOD sign.",
  },
};

const RELEASE = {
  queued: "Queued for HSBC.", dispatching: "Being sent to HSBC.", sent: "Sent to HSBC.",
  rejected: "HSBC rejected the payment.", unknown: "HSBC outcome unknown — Accounting is checking.",
  blocked: "Release blocked — see the ERP journals tab.",
};

// A release that did not go through gets its own red box with the reason, so the problem
// is seen at a glance instead of hiding in one grey line.
const PROBLEM_TITLE = {
  not_sent: "Not sent to HSBC — the HSBC gateway stopped before sending",
  rejected: "HSBC rejected the payment",
  unknown: "Unclear whether HSBC received the payment",
  not_received: "HSBC confirmed it did not receive the payment",
  blocked: "Release blocked",
};

const releaseProblem = (pkg) => {
  if (!["rejected", "unknown", "blocked"].includes(pkg.release_state)) return null;
  const kind = pkg.release_state === "rejected" && pkg.release_not_sent ? "not_sent"
    : pkg.release_state === "unknown" && pkg.release_not_received ? "not_received" : pkg.release_state;
  return { kind, title: PROBLEM_TITLE[kind] };
};

// What the reason box starts with, per problem — Accounting may change it.
const SUGGESTED_REASON = {
  not_sent: "HSBC gateway fixed — nothing reached HSBC before",
  rejected: "Cause fixed after HSBC rejected the payment",
  not_received: "HSBC confirmed it never received the payment",
};

const PROBLEM_HINT = {
  not_sent: "Nothing reached HSBC. Fix the cause, then Accounting uses Send to HSBC again under ERP & bank on the ERP journals tab.",
  unknown: "Nothing is sent again until HSBC's answer is known — check the status, or ask HSBC and record what they say.",
  not_received: "Recorded on the request. Accounting can now send the same signed payment to HSBC again.",
  default: "Fix the cause, then Accounting uses Send to HSBC again under ERP & bank on the ERP journals tab.",
};

const ReleaseProblem = ({ pkg, problem, paymentRequest }) => (
  <div role="alert" aria-label="HSBC release problem"
    className="tw-mt-2 tw-rounded-geist-sm tw-border tw-border-solid tw-border-[#fda29b] tw-bg-[#fef3f2] tw-px-3 tw-py-2 tw-text-[#912018]">
    <p className="tw-m-0 tw-font-semibold">
      <i className="bi bi-x-octagon-fill tw-mr-1.5" aria-hidden="true" />{problem.title}
    </p>
    {pkg.release_error && <p className="tw-m-0 tw-mt-1 tw-font-mono tw-text-[12px] tw-break-words">{pkg.release_error}</p>}
    {pkg.hsbc_trace_id && problem.kind === "unknown" && (
      <p className="tw-m-0 tw-mt-1 tw-text-[12px]">HSBC trace id <code>{pkg.hsbc_trace_id}</code> — quote it when asking HSBC.</p>
    )}
    <p className="tw-m-0 tw-mt-1 tw-text-[12px]">{PROBLEM_HINT[problem.kind] || PROBLEM_HINT.default}</p>
    {problem.kind === "unknown" ? (
      <div className="tw-flex tw-flex-wrap tw-gap-2">
        <CheckHsbcStatusButton paymentRequest={paymentRequest} />
        <ConfirmNotReceivedButton paymentRequest={paymentRequest} />
      </div>
    ) : <RetryReleaseButton paymentRequest={paymentRequest} suggestedReason={SUGGESTED_REASON[problem.kind] || ""} />}
  </div>
);

ReleaseProblem.propTypes = { pkg: PropTypes.object.isRequired, problem: PropTypes.object.isRequired, paymentRequest: PropTypes.object };

/** One line every approver sees on an HSBC online payment: is the file what they sign? */
const HsbcPackageBanner = ({ paymentRequest: pr }) => {
  const pkg = pr?.hsbc_package;
  // No view for "waiting_journal": nothing HSBC to say before the Payment journal (APP) is in ERP.
  const view = pkg && VIEW[pkg.status];
  if (!view) return null;
  const problem = releaseProblem(pkg);

  return (
    <div role="status" aria-label="HSBC payment file"
      className={`tw-flex tw-items-start tw-gap-3 tw-rounded-geist-md tw-border tw-border-solid tw-px-4 tw-py-3 ${view.tone}`}>
      <i className={`bi ${view.icon} tw-mt-0.5 tw-text-[18px]`} aria-hidden="true" />
      <div className="tw-min-w-0 tw-text-[13px] tw-leading-relaxed">
        <p className="tw-m-0 tw-font-semibold">{view.title}</p>
        <p className="tw-m-0">{view.body(pkg)}</p>
        {pkg.status === "ready" && pkg.digest && (
          <p className="tw-m-0 tw-text-[12px] tw-opacity-80">Signed content <code title={pkg.digest}>{pkg.digest.slice(0, 12)}…</code></p>
        )}
        {pkg.release_state && !problem && <p className="tw-m-0 tw-text-[12px] tw-font-semibold">{RELEASE[pkg.release_state] || pkg.release_state}</p>}
        {problem && <ReleaseProblem pkg={pkg} problem={problem} paymentRequest={pr} />}
      </div>
    </div>
  );
};

HsbcPackageBanner.propTypes = { paymentRequest: PropTypes.object };

export default HsbcPackageBanner;
